import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

function isForbiddenError(error: Error) {
  return (
    error.message.includes('Forbidden') ||
    error.message.includes('Not authenticated') ||
    error.message.includes('User record not found')
  );
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  handleGoHome = () => {
    this.setState({ error: null });
    window.location.href = '/dashboard';
  };

  handleRetry = () => {
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;

    if (!error) return this.props.children;

    const isForbidden = isForbiddenError(error);

    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            </svg>
          </div>

          {isForbidden ? (
            <>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h1>
              <p className="text-gray-500 mb-6">
                You don't have permission to view this page.
              </p>
              <button
                onClick={this.handleGoHome}
                className="w-full bg-gray-900 text-white py-2.5 px-4 rounded-lg font-medium hover:bg-gray-700 transition"
              >
                Go to Dashboard
              </button>
            </>
          ) : (
            <>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h1>
              <p className="text-gray-500 mb-2">An unexpected error occurred.</p>
              <p className="text-xs text-gray-400 font-mono mb-6 break-all">{error.message}</p>
              <div className="flex gap-3">
                <button
                  onClick={this.handleRetry}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 px-4 rounded-lg font-medium hover:bg-gray-50 transition"
                >
                  Try Again
                </button>
                <button
                  onClick={this.handleGoHome}
                  className="flex-1 bg-gray-900 text-white py-2.5 px-4 rounded-lg font-medium hover:bg-gray-700 transition"
                >
                  Go to Dashboard
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}
